import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography
} from '@mui/material';
import React, { useState } from 'react';
import ApiClient from '../config/ApiClient';
import { DailyMoodCheckIn } from '../object-actions/types/types';

const MOOD_OPTIONS = {
    okay_steady: {
        emoji: '🙂',
        shortText: "I'm okay and steady.",
        description: "I feel fine, present, and able to handle what's in front of me. Nothing big—just stable and grounded."
    },
    calm_relieved: {
        emoji: '😌',
        shortText: "I feel calm and relieved.",
        description: "My body feels settled. I'm breathing easier and my mind feels clearer. I'm relaxed and at ease."
    },
    strong_confident: {
        emoji: '💪',
        shortText: "I feel strong and confident.",
        description: "I believe in myself right now. I feel capable, motivated, and ready to take on a challenge."
    },
    energized_driven: {
        emoji: '🔥',
        shortText: "I feel energized and driven.",
        description: "I'm locked in. I feel powerful, focused, and ready to push myself. High motivation, high focus."
    },
    centered_control: {
        emoji: '🧘',
        shortText: "I feel centered and in control.",
        description: "My mind and body are balanced. I feel mindful, intentional, and able to respond—not react."
    }
} as const;

interface DailyMoodCheckInModalProps {
    open: boolean;
    onClose: () => void;
    existingCheckIn?: DailyMoodCheckIn | null;
    onSuccess?: () => void;
}

const DailyMoodCheckInModal: React.FC<DailyMoodCheckInModalProps> = ({
    open,
    onClose,
    existingCheckIn,
    onSuccess
}) => {
    const [selectedMood, setSelectedMood] = useState<keyof typeof MOOD_OPTIONS | null>(
        existingCheckIn?.mood_choice as keyof typeof MOOD_OPTIONS || null
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async () => {
        if (!selectedMood) {
            setError('Please select a mood');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Get today's date in user's local timezone as YYYY-MM-DD
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            const checkInDate = `${year}-${month}-${day}`;

            const response = await ApiClient.post('/api/daily-mood-checkins/', {
                mood_choice: selectedMood,
                check_in_date: checkInDate
            });

            if (response.success) {
                onSuccess?.();
                onClose();
            } else {
                setError(response.error || 'Failed to save check-in');
            }
        } catch (err) {
            setError('An error occurred while saving your check-in');
            console.error('Error saving check-in:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setSelectedMood(existingCheckIn?.mood_choice as keyof typeof MOOD_OPTIONS || null);
            setError(null);
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Typography variant="h5" component="div">
                    How are you feeling today?
                </Typography>
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    {(Object.keys(MOOD_OPTIONS) as Array<keyof typeof MOOD_OPTIONS>).map((key) => {
                        const option = MOOD_OPTIONS[key];
                        const isSelected = selectedMood === key;
                        return (
                            <Button
                                key={key}
                                variant={isSelected ? 'contained' : 'outlined'}
                                onClick={() => setSelectedMood(key)}
                                disabled={loading}
                                data-attr="daily-mood-checkin-button-option"
                                data-custom-id={key}
                                sx={{
                                    p: 2,
                                    textAlign: 'left',
                                    justifyContent: 'flex-start',
                                    textTransform: 'none',
                                    minHeight: 'auto',
                                    '&:hover': {
                                        backgroundColor: isSelected ? 'primary.dark' : 'action.hover'
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <Typography variant="h4" component="span">
                                            {option.emoji}
                                        </Typography>
                                        <Typography variant="body1" component="span" fontWeight={isSelected ? 600 : 400}>
                                            {option.shortText}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ ml: 5 }}>
                                        {option.description}
                                    </Typography>
                                </Box>
                            </Button>
                        );
                    })}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || !selectedMood}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                    data-attr="daily-mood-checkin-button-submit"
                >
                    {loading ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DailyMoodCheckInModal;
export { MOOD_OPTIONS };

